import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Zap, Command } from 'lucide-react';
import { searchCatalog } from '../lib/catalog';
import { PlatformLogo } from './PlatformLogo';

const SearchModal = ({ open, onClose }) => {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current.focus(), 60);
    if (!open) setQ('');
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (open && e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const results = useMemo(() => searchCatalog(q), [q]);
  const showEmptyState = q.trim() && results.platforms.length === 0 && results.products.length === 0;

  if (!open) return null;

  const go = (path) => { onClose(); navigate(path); };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[8vh] px-4" role="dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-2xl glass-card rounded-2xl shadow-2xl overflow-hidden fade-up">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
          <Search className="w-5 h-5 text-white/60" />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder="Recherche : TikTok, Spotify plays, avis Google…"
            className="flex-1 bg-transparent outline-none text-white placeholder:text-white/40 text-base" />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/50 text-[10px] font-semibold">ESC</kbd>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/70">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-3">
          {!q.trim() && (
            <div className="p-8 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-4">
                <Zap className="w-3.5 h-3.5 text-[#ff8247]" />
                <span className="text-white/70 text-xs font-semibold">Recherche instantanée</span>
              </div>
              <div className="text-white/60 text-sm mb-1">Tapez pour explorer 40+ réseaux et 100+ services</div>
              <div className="text-white/40 text-xs">Ex : « twitter followers », « avis google », « spotify plays »</div>
            </div>
          )}

          {showEmptyState && (
            <div className="p-8 text-center text-white/60 text-sm">Aucun résultat pour « {q} ».</div>
          )}

          {results.platforms.length > 0 && (
            <div className="mb-3">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold">Réseaux</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {results.platforms.map(p => (
                  <button key={p.id} onClick={() => go(`/collections/${p.slug}`)}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition text-left group">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${p.color}18` }}>
                      <PlatformLogo platform={p.id} className="w-6 h-6" color={p.id === 'instagram' ? undefined : p.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-semibold">{p.name}</div>
                      <div className="text-white/50 text-xs truncate">{p.desc}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#ff8247] group-hover:translate-x-1 transition" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {results.products.length > 0 && (
            <div>
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold">Services</div>
              <div className="space-y-1">
                {results.products.map(p => (
                  <button key={p.id} onClick={() => go(`/product/${p.id}`)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition text-left group">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${p.platform?.color}18` }}>
                      <PlatformLogo platform={p.platformKey} className="w-5 h-5" color={p.platformKey === 'instagram' ? undefined : p.platform?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-semibold truncate">{p.name}</div>
                      <div className="text-white/40 text-xs">{p.platform?.name} · dès {p.variants[0].price.toFixed(2)} €</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#ff8247] group-hover:translate-x-1 transition" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between text-[11px] text-white/40">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Command className="w-3 h-3" /> K pour ouvrir</span>
            <span>↵ pour valider</span>
          </div>
          <span>{results.platforms.length + results.products.length} résultat(s)</span>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
